// Copyright (c) 2018 Cigent Technology Inc. All Rights Reserved.

import { ILicense } from '../types/subscription';
import * as Sentry from '@sentry/browser';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

// Enable the UTC plugin for Day.js
dayjs.extend(utc);

export class LicenseMethods {
    static readonly UNKNOWN = 'Unknown';
    static disconnectThreshold = 900;

    static get_seconds(dateTime: string, now: dayjs.Dayjs): number {
        const _dateAndTime = dayjs.utc(new Date(dateTime));
        const nowUTC = now.utc();
        // Calculate difference in seconds
        return nowUTC.diff(_dateAndTime, 'second');
    }


    /** Set the last_seen_seconds and last_seen time on license passed using license.last_check_dttm and
     * now value passed.  If now value is null, a new data is used.
     *
     * @param license
     * @param now
     * @param disconnect_threshold
     */
    static setLastSeen(license: ILicense, now: dayjs.Dayjs = null, disconnect_threshold = 0): void {
        if ( now == null ) {
            now = dayjs().utc();
        }
        if (license.last_check_dttm !== undefined && license.last_check_dttm != null && license.last_check_dttm !== '0') {
          const dttm =  license.last_check_dttm;
            const seconds = LicenseMethods.get_seconds(dttm, now);
            license.last_seen_seconds = seconds;
          const lastSeenText = LicenseMethods.secondsToDhms(seconds) + ' ago';
          if (disconnect_threshold !== 0 && seconds <= disconnect_threshold) {
              license.last_seen = 'Connected';
          } else {
              license.last_seen = lastSeenText;
          }
        } else {
          license.last_seen_seconds = 0;
          license.last_seen = 'Never';
        }
    }

    static secondsToDhms(seconds: number): string {
        seconds = Number(seconds);
        const d = Math.floor(seconds / (3600 * 24));
        const h = Math.floor(seconds % (3600 * 24) / 3600);
        const m = Math.floor(seconds % 3600 / 60);

        // Build display strings for non-zero values
        const dDisplay = d > 0 ? d + (d === 1 ? ' day' : ' days') : '';
        const hDisplay = h > 0 ? h + (h === 1 ? ' hour' : ' hours') : '';
        const mDisplay = m > 0 ? m + (m === 1 ? ' minute' : ' minutes') : '';

        // Concatenate only non-empty parts and handle fallback
        const display = [dDisplay, hDisplay, mDisplay].filter(Boolean).join(', ');
        return display || '1 minute';
    }

    // https://stackoverflow.com/a/62575586/85326
    static compareVersion(currentVersion: string, minVersion: string): -1 | 0 | 1 {
      if (!currentVersion || !minVersion) {
          return -1;
      }
      const current = currentVersion.split('.').map(x => parseFloat(x));
      const min = minVersion.split('.').map(x => parseFloat(x));

      for (let i = 0; i < Math.max(current.length, min.length); i++) {
        if ((current[i] || 0) < (min[i] || 0)) {
          return -1;
        } else if ((current[i] || 0) > (min[i] || 0)) {
          return 1;
        }
      }
      return 0;
    }

    static getOsVersion(license: ILicense): string {
        let displayName = LicenseMethods.UNKNOWN;
        let fallbackName = LicenseMethods.UNKNOWN;
        const os_version = this.getOsBuildVersion(license)
        if (os_version != null && os_version !== LicenseMethods.UNKNOWN) {
            // Check if os_version contains only numbers and periods (e.g., "10.0.26200")
            if (/^[0-9.]+$/.test(os_version)) {
                for (const {version, display} of osVersionsJson) {
                    const comparison = LicenseMethods.compareVersion(os_version, version);
                    if (comparison === 0) {
                        return display; // Return exact match
                    } else if (comparison > 0 && fallbackName === LicenseMethods.UNKNOWN) {
                        const osParts = display.includes('(') ? display.split('(') : [display];
                        const osName = osParts[0].trim();
                        fallbackName = `${osName} (${os_version})`;
                    }
                }
                // Apply fallback if no exact match
                displayName = fallbackName;
                const msg = `Could not find Windows version, ${os_version}, in osVersionsJson, may need to ` +
                    'update the list of Windows builds in license.methods.ts';
                Sentry.captureMessage(msg);
            } else {
                return os_version;
            }
        }
        return displayName;
    }

    static getOsBuildVersion(license: ILicense): string {
      let os_build_version = LicenseMethods.UNKNOWN;
      if (license?.config) {
          if (license.config?.os_version) {
              os_build_version = license.config?.os_version;
          }
      }
      return os_build_version;
    }

    static getTPMVersion(license: ILicense): string {
      let tpmVersion = '';
      if (license?.config) {
          if (license.config?.tpm) {
              tpmVersion = license.config.tpm.split(',')[0].trim();
          }
      }
      return tpmVersion;
    }

}

// This code modified from stack overflow at:
// https://stackoverflow.com/a/53387532/85326
// Author is: https://stackoverflow.com/users/2930038/vanowm
/*use strict*/
export const compareLicenseVersions: (a: string, b: string) => -1 | 0 | 1 = (() => {

    const generateReplacement = (c: string): string => {
      const cleaned = c.replace(/[\W_]+/, '');
      return `.${cleaned ? cleaned.toLowerCase().charCodeAt(0) - 65536 : ''}.`;
    };

  const normalizeVersion = (t: string): string[] =>
    ('' + t)
      .replace(/[^0-9.]+/g, generateReplacement)
      .replace(/(?:\.0+)*(\.-\d+)(\.\d+)?\.*$/g, '$1$2')
      .split('.');

  return (a: string, b: string): -1 | 0 | 1 => {
    let i: number;
    let r = 0;
    const normalizedA = normalizeVersion(a);
    const normalizedB = normalizeVersion(b);
    const c = Math.max(normalizedA.length, normalizedB.length);
    for (i = 0; i < c && r === 0; i++) {
      const segmentA = normalizedA[i] ? ~~normalizedA[i] : 0;
      const segmentB = normalizedB[i] ? ~~normalizedB[i] : 0;
      r = segmentA < segmentB ? -1 : segmentA > segmentB ? 1 : 0;
    }
    return r as -1 | 0 | 1;
  };
})();

export type OSVersion = {
    display: string;
    version: string;
};

// List of Windows 11 Builds: https://docs.microsoft.com/en-us/windows-insider/flight-hub/
// List of Windows earlier version numbers: https://www.lifewire.com/windows-version-numbers-2625171
export const osVersionsJson: OSVersion[] = [
    {"display": "Windows 11 (26H2)", "version": "10.0.26300"},
    {"display": "Windows 11 (26H1)", "version": "10.0.28000"},
    {"display": "Windows 11 (25H2)", "version": "10.0.26200"},
    {"display": "Windows 11 (24H2)", "version": "10.0.26100"},
    {"display": "Windows 11 (23H2)", "version": "10.0.22631"},
    {"display": "Windows 11 (22H2)", "version": "10.0.22621"},
    {"display": "Windows 11 (21H2)", "version": "10.0.22000"},
    {"display": "Windows 10 (22H2)", "version": "10.0.19045"},
    {"display": "Windows 10 (21H2)", "version": "10.0.19044"},
    {"display": "Windows 10 (21H1)", "version": "10.0.19043"},
    {"display": "Windows 10 (20H2)", "version": "10.0.19042"},
    {"display": "Windows 10 (2004)", "version": "10.0.19041"},
    {"display": "Windows 10 (1909)", "version": "10.0.18363"},
    {"display": "Windows 10 (1903)", "version": "10.0.18362"},
    {"display": "Windows 10 (1809)", "version": "10.0.17763"},
    {"display": "Windows 10 (1803)", "version": "10.0.17134"},
    {"display": "Windows 10 (1709)", "version": "10.0.16299"},
    {"display": "Windows 10 (1703)", "version": "10.0.15063"},
    {"display": "Windows 10 (1607)", "version": "10.0.14393"},
    {"display": "Windows 10 (1511)", "version": "10.0.10586"},
    {"display": "Windows 10", "version": "10.0.10240"},
    {"display": "Windows 8.1 (Update 1)", "version": "6.3.9600"},
    {"display": "Windows 8.1", "version": "6.3.9200"},
    {"display": "Windows 8", "version": "6.2.9200"},
    {"display": "Windows 7 SP1", "version": "6.1.7601"},
    {"display": "Windows 7", "version": "6.1.7600"}
];
